import { pushAnalyticsEvent } from '@/lib/analyticsLoader';
import { sanitizeAnalyticsUrl } from '@/lib/analyticsUrl';

const URL_PARAM_KEYS = ['landing_path', 'page_location', 'page_path', 'page_referrer'] as const;

/** GA4 özel / önerilen olaylar — GTM dataLayer veya gtag üzerinden. */
export function trackGa4Event(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (!params) {
    pushAnalyticsEvent(eventName, params);
    return;
  }
  const safeParams = { ...params };
  for (const key of URL_PARAM_KEYS) {
    const value = safeParams[key];
    if (typeof value === 'string') safeParams[key] = sanitizeAnalyticsUrl(value) || undefined;
  }
  pushAnalyticsEvent(eventName, safeParams);
}

/**
 * Telefon numarasına tıklama — form dışındaki ikinci lead kanalı.
 * Google Ads’te ayrı bir dönüşüm eylemi olarak içe aktarılabilir.
 */
export function trackPhoneClick(lang: string, placement: string): void {
  trackGa4Event('phone_click', {
    lead_source: 'phone',
    placement,
    language: lang,
  });
}

/**
 * İletişim formu başarılı gönderim — GA4’te “generate_lead” (Google Ads dönüşümüne bağlanabilir).
 */
export function trackContactFormLead(
  lang: string,
  attribution?: {
    leadId?: string;
    source?: string;
    channel?: string;
    toolId?: string;
    projectType?: string;
    blogSlug?: string;
    landingPath?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    gclid?: string;
    gbraid?: string;
    wbraid?: string;
    fbclid?: string;
    msclkid?: string;
    referrerClass?: string;
    referrerHost?: string;
    hearAbout?: string;
  } | null,
): void {
  trackGa4Event('generate_lead', {
    currency: 'TRY',
    value: 1,
    lead_id: attribution?.leadId || undefined,
    lead_source: attribution?.source ?? 'contact_form',
    lead_channel: attribution?.channel || undefined,
    tool_id: attribution?.toolId || undefined,
    project_type: attribution?.projectType || undefined,
    blog_slug: attribution?.blogSlug || undefined,
    landing_path: sanitizeAnalyticsUrl(attribution?.landingPath) || undefined,
    referrer_class: attribution?.referrerClass || undefined,
    referrer_host: attribution?.referrerHost || undefined,
    hear_about: attribution?.hearAbout || undefined,
    utm_source: attribution?.utmSource || undefined,
    utm_medium: attribution?.utmMedium || undefined,
    utm_campaign: attribution?.utmCampaign || undefined,
    utm_term: attribution?.utmTerm || undefined,
    utm_content: attribution?.utmContent || undefined,
    gclid: attribution?.gclid || undefined,
    gbraid: attribution?.gbraid || undefined,
    wbraid: attribution?.wbraid || undefined,
    fbclid: attribution?.fbclid || undefined,
    msclkid: attribution?.msclkid || undefined,
    language: lang,
  });
}

/** Blog lead magnet indirme — e-posta karşılığı kaynak. */
export function trackLeadMagnetDownload(blogSlug: string, resourceId: string, lang: string): void {
  trackGa4Event('lead_magnet_download', {
    blog_slug: blogSlug,
    lead_magnet_id: resourceId,
    language: lang,
  });
}

/** Lead magnet bildirimi (EmailJS) başarısız — indirme yine de başlatılır. */
export function trackLeadMagnetEmailError(blogSlug: string, resourceId: string, lang: string): void {
  trackGa4Event('lead_magnet_email_error', {
    blog_slug: blogSlug,
    lead_magnet_id: resourceId,
    language: lang,
  });
}

/** AI/deterministik araç kullanımı — reklam dönüşüm/etkileşim optimizasyonu için. */
export function trackToolUse(toolId: string, status: 'success' | 'error'): void {
  trackGa4Event('tool_use', {
    tool_id: toolId,
    status,
  });
}

export function trackToolResultAction(
  toolId: string,
  action: 'copy_one' | 'copy_all' | 'save' | 'share_whatsapp' | 'share_x' | 'share_linkedin' | 'regenerate',
): void {
  trackGa4Event('tool_result_action', {
    tool_id: toolId,
    action,
  });
}
